import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTravelAutocomplete } from '../../hooks/useTravelAutocomplete';
import { MapPin, Plane, Train } from 'lucide-react';

const GAP = 6;            // px between input and list
const EDGE = 8;           // px kept free from the screen edges
const MIN_LIST = 160;     // below this much room, open above instead
const MAX_LIST = 400;

const isMobileView = () => typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(max-width: 768px)').matches;

const TravelAutocompleteInput = ({
    value,
    onChange,
    placeholder,
    className,
    onSelect,
    maxLength
}) => {
    const { suggestions, loading, search } = useTravelAutocomplete();
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [mobile, setMobile] = useState(isMobileView);
    const inputRef = useRef(null);
    const blurTimerRef = useRef(null);

    useEffect(() => {
        const on = () => setMobile(isMobileView());
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []);

    // Position the list under the input, inside the part of the screen that is actually visible.
    // Before: position:fixed with a fixed 400px height and a 320px minimum width. On phones the
    // keyboard shrinks the visible area (and iOS shifts the visual viewport), so the fixed list
    // landed on top of the input or under the keyboard, and spilled off the right edge.
    useLayoutEffect(() => {
        if (!showSuggestions || !inputRef.current) return undefined;

        const updatePos = () => {
            if (!inputRef.current) return;
            const rect = inputRef.current.getBoundingClientRect();
            const vv = window.visualViewport;
            const viewTop = vv ? vv.offsetTop : 0;
            const viewLeft = vv ? vv.offsetLeft : 0;
            const viewH = vv ? vv.height : window.innerHeight;
            const viewW = vv ? vv.width : window.innerWidth;

            // Width: at least the input, 320 on desktop, never wider than the screen
            const wantW = mobile ? Math.max(rect.width, viewW - EDGE * 2) : Math.max(rect.width, 320);
            const width = Math.min(wantW, viewW - EDGE * 2);
            let left = rect.left;
            if (left + width > viewLeft + viewW - EDGE) left = viewLeft + viewW - EDGE - width;
            if (left < viewLeft + EDGE) left = viewLeft + EDGE;

            // Height: room below the input within the visible area (keyboard excluded)
            const spaceBelow = viewTop + viewH - rect.bottom - GAP - EDGE;
            const spaceAbove = rect.top - viewTop - GAP - EDGE;
            const openUp = spaceBelow < MIN_LIST && spaceAbove > spaceBelow;
            const maxHeight = Math.max(120, Math.min(MAX_LIST, openUp ? spaceAbove : spaceBelow));

            // Absolute in page coordinates: immune to fixed-position quirks while the keyboard is open
            const style = {
                position: 'absolute',
                left: left + window.scrollX,
                width,
                maxHeight,
                zIndex: 99999,
                background: '#fff',
                border: '1px solid #f3f4f6',
                borderRadius: '12px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
                padding: '8px 0',
            };
            if (openUp) style.top = rect.top + window.scrollY - GAP - maxHeight;
            else style.top = rect.bottom + window.scrollY + GAP;
            setDropdownStyle(style);
        };

        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener('resize', updatePos);   // keyboard opening / closing
            vv.addEventListener('scroll', updatePos);
        }
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
            if (vv) {
                vv.removeEventListener('resize', updatePos);
                vv.removeEventListener('scroll', updatePos);
            }
        };
    }, [showSuggestions, suggestions.length, mobile]);

    useEffect(() => () => clearTimeout(blurTimerRef.current), []);

    const handleChange = (e) => {
        const val = e.target.value;
        onChange(val);
        search(val);
        setShowSuggestions(true);
    };

    const handleBlur = () => {
        // Fallback close. Taps on a suggestion don't blur the input (see keepFocus below).
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 150);
    };

    const handleSelect = (item) => {
        clearTimeout(blurTimerRef.current);
        if (onSelect) {
            onSelect(item);
        } else {
            onChange(item.label || item.name || item.description);
        }
        setShowSuggestions(false);
        if (inputRef.current) inputRef.current.blur();
    };

    // Keep focus on the input while the list is being touched, so it doesn't close mid-tap.
    // Selection happens on click, not on pointer down, so scrolling the list on a phone
    // no longer picks whatever row the finger first landed on.
    const keepFocus = (e) => e.preventDefault();

    const getIcon = (type) => {
        let IconComp = MapPin;
        let bgClass = "bg-gray-100";
        let textClass = "text-gray-600";

        if (type === 'station') {
            IconComp = Train;
            bgClass = "bg-orange-100";
            textClass = "text-orange-600";
        } else if (type === 'airport') {
            IconComp = Plane;
            bgClass = "bg-sky-100";
            textClass = "text-sky-600";
        }

        return (
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${bgClass} flex-shrink-0`}>
                <IconComp size={18} strokeWidth={2} className={textClass} />
            </div>
        );
    };

    const suggestionItem = (item, key) => (
        <div
            key={key}
            role="option"
            aria-selected="false"
            className="px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 transition-all duration-200"
            onMouseDown={keepFocus}
            onClick={() => handleSelect(item)}
        >
            <div className="flex-shrink-0">
                {getIcon(item.type)}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="font-semibold text-sm text-gray-900 leading-tight text-left truncate">
                    {item.label}
                </span>
                <span className="text-xs text-gray-500 mt-1 leading-tight text-left truncate">
                    {item.subLabel || item.city || "City"}
                </span>
            </div>
        </div>
    );

    const dropdown = showSuggestions && suggestions.length > 0 && (
        <div
            className="autocomplete-suggestions-container"
            role="listbox"
            data-leadbot-hide=""          // tells Routy to step aside while the list is open
            style={dropdownStyle}
            onMouseDown={keepFocus}
        >
            {(!value || value.length < 1) ? (
                <>
                    <div className="px-4 py-2 text-xs font-bold text-left text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1">
                        Most Searched Airports
                    </div>
                    {suggestions.filter(s => s.type === 'airport').map((item, i) =>
                        suggestionItem(item, `air-${i}`)
                    )}
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1 mt-2 border-t border-gray-100 pt-3 text-left">
                        Most Searched Stations
                    </div>
                    {suggestions.filter(s => s.type === 'station').map((item, i) =>
                        suggestionItem(item, `stat-${i}`)
                    )}
                </>
            ) : (
                suggestions.map((item, i) => suggestionItem(item, i))
            )}
            {loading && (
                <div className="p-3 text-center text-gray-400 text-xs animate-pulse">
                    Getting best routes...
                </div>
            )}
        </div>
    );

    return (
        <div className="relative w-full">
            <input
                ref={inputRef}
                type="text"
                className={className}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                maxLength={maxLength}
                onBlur={handleBlur}
                onFocus={() => {
                    clearTimeout(blurTimerRef.current);
                    search(value);
                    setShowSuggestions(true);
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') setShowSuggestions(false); }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
                // iOS zooms into inputs under 16px, which shifts the page and the list with it
                style={mobile ? { fontSize: 16 } : undefined}
            />
            {/* Portal: renders at document.body, completely outside all stacking contexts */}
            {dropdown && ReactDOM.createPortal(dropdown, document.body)}
        </div>
    );
};

export default TravelAutocompleteInput;
